import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import './PublicExamStyles.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicExamRegistrationPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [exam, setExam] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [attemptId, setAttemptId] = useState(null);
  
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    organization: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [termsAccepted, setTermsAccepted] = useState(false);

  useEffect(() => {
    loadExam();
  }, [slug]);

  const loadExam = async () => {
    try {
      const res = await fetch(`${API_URL}/api/exams/public/${slug}`);
      if (res.ok) {
        const data = await res.json();
        setExam(data);
      } else if (res.status === 404) {
        setError('Examen non trouvé');
      } else {
        throw new Error('Erreur de chargement');
      }
    } catch (error) {
      console.error('Load error:', error);
      setError('Impossible de charger l\'examen');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!form.full_name.trim()) errors.full_name = 'Le nom complet est requis';
    if (!form.email.trim()) {
      errors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      errors.email = 'Email invalide';
    }
    if (!termsAccepted) errors.terms = 'Vous devez accepter les conditions';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Veuillez corriger les erreurs');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/exams/public/${slug}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      
      if (res.ok) {
        const data = await res.json();
        setAttemptId(data.attempt_id);
        
        if (data.resumed) {
          // Candidate has an in-progress exam, skip to take page directly
          toast.info('Reprise de votre session en cours...');
          try {
            if (!document.fullscreenElement) {
              await document.documentElement.requestFullscreen();
            }
          } catch (err) {
            console.warn("Fullscreen activation failed:", err);
          }
          navigate(`/exam/${slug}/take/${data.attempt_id}`);
          return;
        }
        
        setRegistered(true);
        toast.success('Inscription réussie !');
      } else {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Inscription échouée');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const startExam = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      }
    } catch (err) {
      console.warn("Fullscreen activation failed on start:", err);
    }

    if (attemptId) {
      navigate(`/exam/${slug}/take/${attemptId}`);
    }
  };

  // Loading state
  if (loading) {
    return (
      <main className="exam-page">
        <div className="exam-shell">
          <div className="exam-loading">
            <Loader2 className="exam-loading-spinner" />
            <p>Chargement de l'examen...</p>
          </div>
        </div>
      </main>
    );
  }

  // Error state
  if (error) {
    return (
      <main className="exam-page">
        <div className="exam-shell">
          <div className="exam-error-card">
            <AlertCircle className="exam-error-icon" />
            <h2>Erreur</h2>
            <p>{error}</p>
          </div>
        </div>
      </main>
    );
  }

  // Success - Ready to start
  if (registered) {
    return (
      <main className="exam-page">
        <div className="exam-shell">
          <header className="exam-hero">
            <span className="exam-badge">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M8 2v4"></path>
                <path d="M16 2v4"></path>
                <rect x="3" y="4" width="18" height="18" rx="3"></rect>
                <path d="M3 10h18"></path>
              </svg>
              Examen en ligne
            </span>
            <h1>{exam?.title}</h1>
          </header>

          <div className="exam-success-card">
            <CheckCircle className="exam-success-icon" />
            <h2>Inscription réussie !</h2>
            <p>Vous êtes prêt à passer l'examen <strong>{exam?.title}</strong>.</p>
            
            <div className="exam-info-box">
              <h4>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 6v6l4 2"></path>
                </svg>
                Avant de commencer
              </h4>
              <ul>
                <li>Assurez-vous d'avoir une connexion internet stable</li>
                <li>Durée : <strong>{exam?.duration_minutes || exam?.settings?.duration_minutes || 60} minutes</strong></li>
                <li>Nombre de questions : <strong>{exam?.question_count || 0}</strong></li>
                {exam?.anti_cheating_enabled && (
                  <li>Mode surveillance activé (ne quittez pas la page)</li>
                )}
              </ul>
            </div>

            <button onClick={startExam} className="exam-button exam-button-primary exam-button-large">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="5 3 19 12 5 21 5 3"></polygon>
              </svg>
              Commencer l'examen
            </button>
          </div>
        </div>
      </main>
    );
  }

  // Registration form
  return (
    <main className="exam-page">
      <div className="exam-shell">
        <header className="exam-hero">
          <span className="exam-badge">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M8 2v4"></path>
              <path d="M16 2v4"></path>
              <rect x="3" y="4" width="18" height="18" rx="3"></rect>
              <path d="M3 10h18"></path>
            </svg>
            Examen en ligne
          </span>
          <h1>{exam?.title}</h1>
          {exam?.description && <p>{exam.description}</p>}
        </header>

        <section className="exam-layout">
          {/* Sidebar */}
          <aside className="exam-glass-card exam-sidebar">
            <div className="exam-sidebar-head">
              <div className="exam-sidebar-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8">
                  <path d="M9 7h6"></path>
                  <path d="M9 11h6"></path>
                  <path d="M9 15h4"></path>
                  <path d="M7 3h7l5 5v11a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z"></path>
                  <path d="M14 3v5h5"></path>
                </svg>
              </div>
              <h2>Informations</h2>
            </div>

            <div className="exam-stats">
              <div className="exam-stat">
                <span className="exam-stat-label">Durée</span>
                <span className="exam-pill">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
                    <circle cx="12" cy="12" r="8"></circle>
                    <path d="M12 8v4l3 2"></path>
                  </svg>
                  {exam?.duration_minutes || exam?.settings?.duration_minutes || 60} min
                </span>
              </div>

              <div className="exam-stat">
                <span className="exam-stat-label">Questions</span>
                <span className="exam-pill">{exam?.question_count || 0}</span>
              </div>

              <div className="exam-stat">
                <span className="exam-stat-label">Score minimum</span>
                <span className="exam-pill">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M12 6v6l4 2"></path>
                  </svg>
                  {exam?.passing_score || 50}%
                </span>
              </div>

              {exam?.category && (
                <div className="exam-stat">
                  <span className="exam-stat-label">Catégorie</span>
                  <span className="exam-pill">{exam.category}</span>
                </div>
              )}
            </div>

            <div className="exam-sidebar-note">
              Assurez-vous d'avoir une connexion stable et de disposer du temps nécessaire avant de commencer l'examen.
            </div>
          </aside>

          {/* Content */}
          <div className="exam-content-col">
            <section className="exam-main-card">
              <div className="exam-main-inner">
                <div className="exam-form-header">
                  <span className="exam-form-topline">Accès candidat</span>
                  <h2 className="exam-form-title">
                    <span className="exam-form-title-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
                        <path d="M16 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2"></path>
                        <circle cx="9.5" cy="7" r="3.5"></circle>
                        <path d="M20 8v6"></path>
                        <path d="M23 11h-6"></path>
                      </svg>
                    </span>
                    Inscription à l'examen
                  </h2>
                  <p className="exam-form-subtitle">
                    Remplissez ce formulaire pour accéder à l'examen et démarrer votre session en toute sécurité.
                  </p>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="exam-form-grid">
                    <div className={`exam-field ${formErrors.full_name ? 'exam-field-error' : ''}`}>
                      <label htmlFor="fullname">Nom complet *</label>
                      <div className="exam-input-wrap">
                        <svg className="exam-input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
                          <path d="M20 21a8 8 0 0 0-16 0"></path>
                          <circle cx="12" cy="8" r="4"></circle>
                        </svg>
                        <input
                          className="exam-input"
                          id="fullname"
                          type="text"
                          placeholder="Jean Dupont"
                          value={form.full_name}
                          onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                        />
                      </div>
                      {formErrors.full_name && <span className="exam-field-error-text">{formErrors.full_name}</span>}
                    </div>

                    <div className={`exam-field ${formErrors.email ? 'exam-field-error' : ''}`}>
                      <label htmlFor="email">Email *</label>
                      <div className="exam-input-wrap">
                        <svg className="exam-input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
                          <rect x="3" y="5" width="18" height="14" rx="2"></rect>
                          <path d="M3 7l9 6 9-6"></path>
                        </svg>
                        <input
                          className="exam-input"
                          id="email"
                          type="email"
                          placeholder="jean@example.com"
                          value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                        />
                      </div>
                      {formErrors.email && <span className="exam-field-error-text">{formErrors.email}</span>}
                    </div>

                    <div className="exam-field">
                      <label htmlFor="phone">Téléphone (optionnel)</label>
                      <div className="exam-input-wrap">
                        <svg className="exam-input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.08 4.18 2 2 0 0 1 4.06 2h3a2 2 0 0 1 2 1.72c.12.9.33 1.78.63 2.62a2 2 0 0 1-.45 2.11L8 9.91a16 16 0 0 0 6.09 6.09l1.46-1.24a2 2 0 0 1 2.11-.45c.84.3 1.72.51 2.62.63A2 2 0 0 1 22 16.92z"></path>
                        </svg>
                        <input
                          className="exam-input"
                          id="phone"
                          type="tel"
                          placeholder="+33 6 12 34 56 78"
                          value={form.phone}
                          onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="exam-field">
                      <label htmlFor="org">Organisation (optionnel)</label>
                      <div className="exam-input-wrap">
                        <svg className="exam-input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
                          <path d="M3 21h18"></path>
                          <path d="M5 21V7l7-4 7 4v14"></path>
                          <path d="M9 9h.01"></path>
                          <path d="M9 13h.01"></path>
                          <path d="M15 9h.01"></path>
                          <path d="M15 13h.01"></path>
                        </svg>
                        <input
                          className="exam-input"
                          id="org"
                          type="text"
                          placeholder="Nom de l'entreprise"
                          value={form.organization}
                          onChange={(e) => setForm({ ...form, organization: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>

                  <div className={`exam-consent ${formErrors.terms ? 'exam-consent-error' : ''}`}>
                    <div className="exam-consent-row">
                      <input
                        id="terms"
                        type="checkbox"
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                      />
                      <div className="exam-consent-content">
                        <strong>J'accepte les conditions</strong>
                        <p>
                          Je m'engage à passer cet examen de manière honnête et à ne pas partager les questions.
                        </p>
                      </div>
                    </div>
                    {formErrors.terms && <span className="exam-field-error-text">{formErrors.terms}</span>}
                  </div>

                  <div className="exam-actions">
                    <button
                      type="submit"
                      className="exam-button exam-button-primary"
                      disabled={submitting}
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="exam-button-spinner" />
                          Inscription en cours...
                        </>
                      ) : (
                        <>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 12a9 9 0 1 1-3.2-6.9"></path>
                            <path d="M21 4v6h-6"></path>
                          </svg>
                          S'inscrire et commencer
                        </>
                      )}
                    </button>

                    <div className="exam-trust-row">
                      <span className="exam-trust">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                        </svg>
                        Accès sécurisé
                      </span>
                      <span className="exam-trust">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="9"></circle>
                          <path d="M12 7v5l3 2"></path>
                        </svg>
                        Démarrage immédiat
                      </span>
                      <span className="exam-trust">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 6L9 17l-5-5"></path>
                        </svg>
                        Simple et rapide
                      </span>
                    </div>

                    <div className="exam-footer-text">
                      En vous inscrivant, vous acceptez les règles de participation à cet examen.
                    </div>
                  </div>
                </form>
              </div>
            </section>

            {/* Proctoring Alert */}
            {exam?.anti_cheating_enabled && (
              <section className="exam-proctor-alert">
                <div className="exam-proctor-alert-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 3l7 3v5c0 5-3.5 8.5-7 10-3.5-1.5-7-5-7-10V6l7-3z"></path>
                  </svg>
                </div>
                <div className="exam-proctor-alert-content">
                  <h3>Mode surveillé</h3>
                  <p>Cet examen est surveillé. Ne quittez pas la page pendant l'examen.</p>
                </div>
              </section>
            )}
          </div>
        </section>
      </div>
    </main>
  );
}
